import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Info, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import useAdminLoad from "@/hooks/useAdminLoad";
import { LoadState, PageHeader } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import PackTable from "@/components/admin/PackTable";
import SubscriptionTable from "@/components/admin/SubscriptionTable";
import DarkLifetimeCard, { darkChanges, darkError, darkPayload, toDarkRow } from "@/components/admin/DarkLifetimeCard";
import StoreOffersTable, { newOfferRow, offersChanged, offersPayload, toOfferRow, validateOffers } from "@/components/admin/StoreOffersTable";
import { api } from "@/lib/api";
import { PACK_GROUPS, diffPacks, newRow, serverRowErrors, toPayload, toRow, validateRows } from "@/lib/pricing";
import { SUB_TIERS, diffSubs, serverSubErrors, subsPayload, toSubRows, validateSubs } from "@/lib/subs";

export default function Pricing() {
  const { data, error, loading, reload, setData } = useAdminLoad("/admin/pricing");
  const [rows, setRows] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [serverMsg, setServerMsg] = useState(null);
  const [subRows, setSubRows] = useState([]);
  const original = useMemo(() => data?.super_crush_packs || [], [data]);
  const originalSubs = useMemo(() => data?.subscription_products || [], [data]);
  const originalOffers = useMemo(() => data?.store_offers || [], [data]);
  const [offerRows, setOfferRows] = useState([]);
  const originalDark = data?.dark_lifetime || null;
  const [darkRow, setDarkRow] = useState(null);
  const reset = useCallback(() => { setRows(original.map(toRow)); setSubRows(toSubRows(originalSubs)); setOfferRows(originalOffers.map(toOfferRow)); setDarkRow(toDarkRow(originalDark)); }, [original, originalSubs, originalOffers, originalDark]);
  useEffect(() => { reset(); setServerErrors({}); setServerMsg(null); }, [reset]);

  const darkErr = darkError(darkRow);
  const errors = { ...validateRows(rows), ...validateSubs(subRows), ...validateOffers(offerRows), ...(darkErr ? { dark: darkErr } : {}) };
  const shownErrors = { ...serverErrors, ...errors };
  const packChanges = diffPacks(original, rows);
  const subChanges = diffSubs(originalSubs, subRows);
  const offerChange = offersChanged(originalOffers, offerRows);
  const darkChange = darkChanges(originalDark, darkRow);
  const changes = [...subChanges, ...packChanges, ...(offerChange ? [`Store offers: ${originalOffers.length} → ${offerRows.length} (list replaced)`] : []), ...darkChange];
  const invalid = Object.keys(errors).length > 0;

  const updateSub = (k, field, value) => {
    setSubRows((rs) => rs.map((r) => (r.k === k ? { ...r, [field]: value } : r)));
    setServerErrors((e) => { const n = { ...e }; delete n[k]; return n; });
    setServerMsg(null);
  };

  const update = (k, field, value) => {
    setRows((rs) => rs.map((r) => (r.k === k ? { ...r, [field]: value } : r)));
    setServerErrors((e) => { const n = { ...e }; delete n[k]; return n; });
    setServerMsg(null);
  };

  const save = async () => {
    try {
      // Only send the sections that changed (packs are replaced wholesale; subscriptions are upserted).
      const body = {};
      if (subChanges.length) body.subscription_products = subsPayload(subRows);
      if (packChanges.length) body.super_crush_packs = toPayload(rows);
      if (offerChange) body.store_offers = offersPayload(offerRows);
      if (darkChange.length) body.dark_lifetime = darkPayload(darkRow);
      setData(await api("/admin/pricing", { method: "PUT", body }));
      toast.success("Pricing saved.");
    } catch (e) {
      if (e.code === "offer_in_use" || e.code === "duplicate_offer") { setServerMsg(e.message); return; }
      setServerErrors({ ...(e.data?.length && e.data[0]?.loc?.[1] === "subscription_products" ? serverSubErrors(e, subRows) : serverRowErrors(e, rows)) });
      setServerMsg(e.message);
    }
  };

  return (
    <div data-testid="admin-pricing-page" className={changes.length ? "pb-24" : ""}>
      <PageHeader title="Pricing" description="Subscriptions, Super Crush packs, the Dark look and the store offers Promotions can use." />
      <div className="cw-bg-tint mb-6 flex items-start gap-3 rounded-2xl px-4 py-3 text-sm" data-testid="pricing-note">
        <Info size={18} className="cw-brand-deep mt-0.5 shrink-0" aria-hidden />
        <p className="cw-brand-deep">Prices must match App Store Connect / Play Console. Crushr and Winkr share prices. Purchases go live in a later build (Apple/Google).</p>
      </div>
      {serverMsg && (
        <div className="cw-tone-error mb-6 flex items-start gap-3 rounded-2xl px-4 py-3 text-sm" role="alert" data-testid="pricing-server-error">
          <WarningCircle size={18} className="mt-0.5 shrink-0" aria-hidden /><p>{serverMsg}</p>
        </div>
      )}
      <LoadState loading={loading} error={error} onRetry={reload}>
        <section className="space-y-4" aria-labelledby="subs-title" data-testid="pricing-subscriptions">
          <div><h2 id="subs-title" className="text-lg font-semibold">Subscriptions</h2>
            <p className="cw-muted text-xs">Each tier and period is one store product. Changing a store product ID keeps the old ID under Legacy so existing subscribers still renew.</p></div>
          {SUB_TIERS.map((t) => (
            <SubscriptionTable key={t.key} tier={t} rows={subRows.filter((r) => r.tier === t.key)} errors={shownErrors} onChange={updateSub}
              crushrRows={subRows.filter((r) => r.tier === "crushr")} />
          ))}
        </section>
        <div className="mt-10 space-y-6" data-testid="pricing-packs">
          <div><h2 className="text-lg font-semibold">Super Crush packs</h2><p className="cw-muted text-xs">Saving packs replaces the whole pack list.</p></div>
          {PACK_GROUPS.map((g) => (
            <PackTable key={g.key} group={g} rows={rows.filter((r) => r.tier_group === g.key)} errors={shownErrors} onChange={update}
              onAdd={() => setRows((rs) => [...rs, newRow(g.key, rs)])} onRemove={(k) => setRows((rs) => rs.filter((r) => r.k !== k))} />
          ))}
        </div>
        <StoreOffersTable rows={offerRows} errors={shownErrors} products={originalSubs.map((p) => p.store_product_id).filter(Boolean)}
          onChange={(k, f, v) => { setOfferRows((rs) => rs.map((r) => (r.k === k ? { ...r, [f]: v } : r))); setServerMsg(null); }}
          onAdd={() => setOfferRows((rs) => [...rs, newOfferRow()])} onRemove={(k) => setOfferRows((rs) => rs.filter((r) => r.k !== k))} />
        <DarkLifetimeCard row={darkRow} error={darkErr} onChange={(f, v) => { setDarkRow((r) => ({ ...r, [f]: v })); setServerMsg(null); }} />
      </LoadState>
      {data && changes.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-white/90 backdrop-blur-md lg:left-64" data-testid="pricing-save-bar">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
            <p className="text-sm" role="status" data-testid="pricing-change-count">
              {invalid ? <span className="cw-error">Fix the highlighted fields to save.</span> : <span className="cw-soft">{changes.length} unsaved change{changes.length === 1 ? "" : "s"}</span>}
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={reset} data-testid="pricing-discard-button">Discard</Button>
              <Button onClick={() => setConfirm(true)} disabled={invalid} data-testid="pricing-save-button">Review and save</Button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} destructive={false} testId="pricing-confirm-dialog"
        title="Save these pricing changes?" confirmLabel="Save pricing" onConfirm={save}
        description={packChanges.length ? "Subscriptions are updated and the pack list is replaced with what you see on this page." : "Only the items listed below change."}>
        <ul className="max-h-64 space-y-1.5 overflow-y-auto rounded-xl bg-[var(--cw-surface-tertiary)] p-4 text-sm" data-testid="pricing-confirm-changes">
          {changes.map((c, i) => <li key={i} className="tabular-nums">{c}</li>)}
        </ul>
      </ConfirmDialog>
    </div>
  );
}
